/**
 * useMapLayers Hook
 * Manages layer initialization and toggling
 * Uses MapManager for layer operations
 * Handles layer visibility and ordering
 * Lazy loads layers on demand
 *
 * Key resilience features:
 * - Detects style reloads via layerStatus reset and re-initializes layers.
 * - Uses withMapLayerRetry for background layer initialization.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useMapbox } from '@/contexts/MapboxContext';
import { imagePreloader } from '@/services/map/ImagePreloader';
import { layerRegistry } from '@/services/map/layerRegistry';
import { mapManager } from '@/services/map/MapManager';
import { useMapStore } from '@/stores/slices/useMapStore';

interface UseMapLayersOptions {
  getToken?: () => Promise<string>;
  onLayersInitialized?: () => void;
  onPopupClick?: (type: string) => void;
}

/**
 * Hook for managing map layers
 * Initializes layers on map ready and provides layer control methods
 */
export function useMapLayers(options: UseMapLayersOptions = {}) {
  const { map, isMapReady } = useMapbox();
  const { getToken, onLayersInitialized, onPopupClick } = options;
  const layersInitializedRef = useRef(false);
  const setLayerStatus = useMapStore((state) => state.setLayerStatus);
  const baseLayersStatus = useMapStore((state) => state.layerStatus['base-layers']?.status);

  /**
   * Initialize DEFAULT layers only at startup
   * Ports and incidents are handled by useApiLayers via React Query — not here.
   * OPTIONAL and FEATURE layers are loaded on-demand via layer panel toggles.
   */
  const initializeDefaultLayers = useCallback(async () => {
    if (!map.current) return;

    // EEZ and starterPopups are non-API layers — load them in parallel
    await Promise.allSettled([
      layerRegistry.initializeLayer('eez', map.current).catch((err) => {
        console.error('useMapLayers: Error initializing EEZ layer:', err);
      }),
      layerRegistry.initializeLayer('starterPopups', map.current, onPopupClick).catch((err) => {
        console.error('useMapLayers: Error initializing starterPopups layer:', err);
      }),
    ]);
  }, [map, onPopupClick]);

  /**
   * Initialize critical layers first
   * NOTE: API-based layers (incidents, ports, militaryLocations) are now managed
   * by useApiLayers hook via React Query. This function only initializes
   * non-API layers and sets up the MapManager.
   */
  const initializeCriticalLayers = useCallback(async () => {
    // Wait until map is ready AND we have a valid getToken function
    if (!map.current || !isMapReady || layersInitializedRef.current || !getToken) {
      return;
    }

    try {
      setLayerStatus('base-layers', 'loading');
      // Initialize MapManager with map instance
      mapManager.initialize(map.current);

      // Preload vessel icons immediately with map instance.
      // Await to ensure icons are registered before vessel layers are rendered.
      await imagePreloader.preloadVesselIcons(map.current);

      // API layers (incidents, ports, militaryLocations) are now handled by useApiLayers
      // via React Query. No need to initialize them here.

      // Mark as initialized
      layersInitializedRef.current = true;

      // Initialize DEFAULT layers before signaling readiness.
      // OPTIONAL and FEATURE layers are loaded on-demand via layer panel toggles.
      await initializeDefaultLayers();

      if (onLayersInitialized) {
        onLayersInitialized();
      }

      setLayerStatus('base-layers', 'ready');
    } catch (error) {
      console.error('useMapLayers: Error initializing critical layers:', error);
      setLayerStatus('base-layers', 'error', (error as Error).message);
    }
  }, [map, isMapReady, getToken, onLayersInitialized, initializeDefaultLayers, setLayerStatus]);

  // Initialize layers when map is ready
  // Also re-initialize if base-layers status was reset (style reload)
  useEffect(() => {
    if (isMapReady && map.current) {
      // If status was reset to 'idle', allow re-initialization
      if (baseLayersStatus === 'idle' && layersInitializedRef.current) {
        layersInitializedRef.current = false;
      }
      initializeCriticalLayers();
    }
  }, [isMapReady, map, initializeCriticalLayers, baseLayersStatus]);

  return {
    layersInitialized: layersInitializedRef.current,
  };
}
